<template>
  <div class="space-y-4">
    <LoadingIndicator v-if="!run" message="Loading run..." />
    <template v-else>
      <div class="flex items-center gap-3 flex-wrap">
        <HermesBadge :value="run.status" />
        <span class="text-gray-900 font-medium">Run {{ run.id }}</span>
        <span class="text-gray-500" :title="absolute(run.run_at)">{{ relative(run.run_at) }}<template v-if="run.run_by_name"> · {{ run.run_by_name }}</template></span>
      </div>

      <AgentRunOutput
        :status="run.status"
        :error="run.error_message || ''"
        :console-text="run.console_output || ''"
        :outputs="outputs"
        :plot-urls="egressBlocked ? {} : plotUrls"
        :plots-hidden="egressBlocked"
      />

      <div v-if="produced.length" class="space-y-2">
        <div class="text-gray-500 uppercase tracking-wider">Produces</div>
        <div v-for="f in produced" :key="f.frame_id" class="flex items-center gap-3 min-w-0">
          <router-link :to="framePath(f.frame_id)" class="sensitive text-neutral-800 truncate">{{ f.name }}</router-link>
          <span class="text-gray-500 shrink-0">{{ f.row_count.toLocaleString() }} rows</span>
          <button type="button" class="btn-secondary-sm shrink-0" @click="download(f)">Download CSV</button>
        </div>
        <div v-if="downloadError" class="text-red-dark">{{ downloadError }}</div>
      </div>

      <details v-if="run.console_output && !live" class="rounded border border-gray-200">
        <summary class="cursor-pointer px-3 py-2 text-gray-700">Console</summary>
        <pre class="sensitive px-3 py-2 bg-gray-900 text-gray-100 overflow-x-auto max-h-80 whitespace-pre-wrap" data-demo-blur>{{ run.console_output }}</pre>
      </details>
      <details v-if="run.script_snapshot" class="rounded border border-gray-200">
        <summary class="cursor-pointer px-3 py-2 text-gray-700">Script this run executed</summary>
        <pre class="px-3 py-2 bg-gray-900 text-gray-100 overflow-x-auto max-h-96">{{ run.script_snapshot }}</pre>
      </details>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import AgentRunOutput, { plotKey } from '@/components/Beta/AgentRunOutput.vue';
import HermesBadge from '@/components/Admin/Hermes/HermesBadge.vue';
import { absolute, relative } from '@/components/Admin/Hermes/hermes';
import { downloadFrame } from '@/utils/frameDownload';
import { sensitiveEgressBlocked } from '@/utils/demoEgress';

const POLL_MS = 3000;

export default {
  name: 'StudyRunReport',
  components: { LoadingIndicator, AgentRunOutput, HermesBadge },
  props: {
    runId: { type: Number, required: true },
  },
  emits: ['settled'],
  data() {
    return { run: null, plotUrls: {}, plotEpoch: 0, timer: null, downloadError: '' };
  },
  computed: {
    egressBlocked() {
      return sensitiveEgressBlocked();
    },
    live() {
      return !!this.run && ['pending', 'running'].includes(this.run.status);
    },
    outputs() {
      return ((this.run && this.run.results && this.run.results.outputs) || []).filter((o) => o.kind !== 'frame');
    },
    produced() {
      return ((this.run && this.run.frames) || []).filter((f) => String(f.role).startsWith('output:'));
    },
  },
  watch: {
    runId() {
      this.load();
    },
    // Plot PNGs are drawn server-side from the study's own R script, so their
    // labels cannot be masked. Drop them when Demo Mode starts; fetch again
    // when it ends.
    egressBlocked(blocked) {
      if (blocked) this.dropPlots();
      else if (this.run && !this.live) this.loadPlots();
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    this.dropPlots();
  },
  methods: {
    absolute,
    relative,
    framePath(id) {
      return `/insight/impact-analytics/frames/${id}`;
    },
    async load() {
      clearTimeout(this.timer);
      const { data } = await axios.get(`api/impact_analytics/study-runs/${this.runId}/`);
      if (data.id !== this.runId) return;
      this.run = { ...data, run_by_name: data.run_by_name || '' };
      if (this.live) {
        this.timer = setTimeout(() => this.load(), POLL_MS);
        return;
      }
      this.$emit('settled', data);
      this.loadPlots();
    },
    async loadPlots() {
      this.dropPlots();
      if (sensitiveEgressBlocked()) return;
      const epoch = this.plotEpoch;
      const urls = {};
      for (const output of this.outputs.filter((o) => o.kind === 'plot')) {
        const file = plotKey(output);
        const name = file.replace(/^plots\//, '').replace(/\.png$/, '');
        try {
          const response = await axios.get(`api/impact_analytics/study-runs/${this.runId}/artifact/`,
            { params: { name }, responseType: 'blob' });
          // Demo Mode started (or the run changed) while this was in flight.
          if (sensitiveEgressBlocked() || epoch !== this.plotEpoch) {
            Object.values(urls).forEach((url) => url && URL.revokeObjectURL(url));
            return;
          }
          urls[file] = URL.createObjectURL(response.data);
        } catch {
          urls[file] = '';
        }
      }
      // A later artifact may have failed after Demo Mode started.
      if (sensitiveEgressBlocked() || epoch !== this.plotEpoch) {
        Object.values(urls).forEach((url) => url && URL.revokeObjectURL(url));
        return;
      }
      this.plotUrls = urls;
    },
    dropPlots() {
      this.plotEpoch += 1;
      Object.values(this.plotUrls).forEach((url) => url && URL.revokeObjectURL(url));
      this.plotUrls = {};
    },
    async download(f) {
      this.downloadError = await downloadFrame({ id: f.frame_id, name: f.name }) || '';
    },
  },
};
</script>
